import { Grid, Typography } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ChevronRight } from "@material-ui/icons";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { InfoTooltip } from "../../components/InfoTooltip/info-tooltip.component";
import { EmptyDetailsState } from "../../components/empty-details-state";
import { getParticipantHighlights } from "../../learning/learning.service";
import { Button } from "../../shared/components/button";
import { Highlight } from "../../shared/components/highlights";
import { useHistory } from "../../shared/hooks/useHistory";
import usePricingModal from "../../shared/hooks/usePricingModal";
import { RootState } from "../../store";
import { sectionCollectiblesHighlights } from "./shared-highlights.const";
import { useSharedHighlightsStyles } from "./shared-highlights.styles";

const PAGE_SIZE = 3;

type Props = {
  filterBy?: string;
  hideOnEmptyFilterResult?: boolean;
};

export const SharedHighlights = ({
  filterBy,
  hideOnEmptyFilterResult,
}: Props) => {
  const history = useHistory();
  const classes = useSharedHighlightsStyles();
  const { modalComponent, togglePricingModal } = usePricingModal();

  const isPremium = useSelector<RootState, boolean>(
    (state) => state.profile.isPremium
  );

  const { data, isFetching } = useQuery({
    queryKey: ["highlights-in-collectibles", filterBy],
    queryFn: () =>
      getParticipantHighlights({
        keyword: filterBy,
        pageNumber: 1,
        pageSize: PAGE_SIZE,
      }),
    refetchOnWindowFocus: false,
    enabled: isPremium,
  });

  const showCTAButtons = !isFetching && !data?.totalRecords && !filterBy;
  const showViewMoreButtons =
    (!isFetching && data?.totalRecords > PAGE_SIZE) || filterBy;

  if (hideOnEmptyFilterResult && filterBy && !data?.totalRecords) {
    return null;
  }

  const handleButtonClick = () => {
    if (isPremium) {
      history.push("/participant/highlights");
    } else {
      togglePricingModal();
    }
  };

  return (
    <>
      <Grid item md={12}>
        <Grid item container justifyContent="space-between">
          <Typography className={classes.header}>
            {sectionCollectiblesHighlights.header}
            <InfoTooltip
              title={
                <Typography className={classes.tooltipTitle}>
                  {sectionCollectiblesHighlights.tooltip}
                </Typography>
              }
              placement="right"
            >
              <InfoOutlinedIcon className={classes.infoIcon} />
            </InfoTooltip>
          </Typography>
          {showViewMoreButtons ? (
            <Button
              variant="outlined"
              endIcon={<ChevronRight />}
              className={classes.viewMoreBtn}
              onClick={handleButtonClick}
            >
              View more
            </Button>
          ) : null}
        </Grid>
        <Grid container className={classes.contentContainer}>
          {isFetching ? (
            <CircularProgress />
          ) : !data?.totalRecords ? (
            <EmptyDetailsState
              className={classes.emptyState}
              title={sectionCollectiblesHighlights.title}
              imageUrl={sectionCollectiblesHighlights.imageUrl}
              description={sectionCollectiblesHighlights.description}
              action={
                showCTAButtons ? (
                  <Button
                    variant="outlined"
                    className={classes.button}
                    onClick={handleButtonClick}
                  >
                    {sectionCollectiblesHighlights.buttonText}
                  </Button>
                ) : null
              }
            />
          ) : (
            data?.data.map((highlight) => (
              <Highlight
                id={`highlight-${highlight.id}`}
                title={highlight.type}
                description={highlight.title}
                date={new Date(highlight.createdAt)}
              />
            ))
          )}
        </Grid>
      </Grid>
      {modalComponent}
    </>
  );
};
